import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "context/auth-context";
import { workspaceApi } from "pages/app/workspace/services";

type UseScopedSchoolSelectionOptions = {
  allowEmptySelection?: boolean;
  enabled?: boolean;
  manageActiveSchoolId?: boolean;
  portalAccess: any;
  queryKey?: (string | number)[];
};

export const useScopedSchoolSelection = ({
  allowEmptySelection = false,
  enabled = true,
  manageActiveSchoolId = false,
  portalAccess,
  queryKey = ["workspace-schools"],
}: UseScopedSchoolSelectionOptions) => {
  const { activeSchoolId, setActiveSchoolId } = useAuth();
  const schoolsQuery = useQuery({
    queryKey,
    queryFn: () => workspaceApi.schools.list(),
    enabled,
  });

  const scopedSchoolIds = useMemo(
    () => (portalAccess?.scope?.school_ids || []).map((schoolId: string | number) => String(schoolId)),
    [portalAccess?.scope?.school_ids],
  );
  const hasGlobalSchoolScope = Boolean(portalAccess?.scope?.global) || scopedSchoolIds.length === 0;

  const schools = useMemo(() => {
    const data = schoolsQuery.data || [];

    if (hasGlobalSchoolScope) {
      return data;
    }

    return data.filter((school: any) => scopedSchoolIds.includes(String(school.id)));
  }, [hasGlobalSchoolScope, scopedSchoolIds, schoolsQuery.data]);

  const schoolIds = useMemo(() => schools.map((school: any) => String(school.id)), [schools]);
  const schoolId = activeSchoolId ?? "";

  // Only the global School Ops header (manageActiveSchoolId: true) should auto-pick
  // a default school. Sections read activeSchoolId; URL sync lives in AppLayout.
  useEffect(() => {
    if (!manageActiveSchoolId || !enabled || schoolIds.length === 0) {
      return;
    }

    const currentId = activeSchoolId ? String(activeSchoolId) : "";
    const hasValidSelection = Boolean(currentId) && schoolIds.includes(currentId);

    if (hasValidSelection) {
      return;
    }

    // Keep scoped selections while the schools list is still catching up.
    if (currentId && !hasGlobalSchoolScope && scopedSchoolIds.includes(currentId)) {
      return;
    }

    if (allowEmptySelection) {
      if (currentId) {
        setActiveSchoolId(null);
      }
      return;
    }

    const scopedMatch = scopedSchoolIds.find((id) => schoolIds.includes(id));
    const nextSchoolId = scopedMatch || schoolIds[0];

    if (nextSchoolId && String(activeSchoolId) !== nextSchoolId) {
      setActiveSchoolId(nextSchoolId);
    }
  }, [
    activeSchoolId,
    allowEmptySelection,
    enabled,
    hasGlobalSchoolScope,
    manageActiveSchoolId,
    schoolIds,
    scopedSchoolIds,
    setActiveSchoolId,
  ]);

  const handleSchoolChange = (nextSchoolId: string) => {
    setActiveSchoolId(nextSchoolId || null);
  };

  const selectedSchool = useMemo(
    () => schools.find((school: any) => String(school.id) === String(activeSchoolId)),
    [activeSchoolId, schools],
  );

  return {
    handleSchoolChange,
    schoolId,
    schools,
    schoolsQuery,
    selectedSchool,
    setSchoolId: setActiveSchoolId,
  };
};
